import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import Table from '@/components/ui/tables/Table';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { LinkButton } from '@/components/ui/buttons/LinkButton';
import { Selector } from '@/components/ui/inputs/selector';
import { IndicatorCard } from '@/components/ui/cards/IndicatorCard';
import { useIncidentManagementList } from '@/hooks/attendance/management/useIncidentManagementList';
import { useIncidentManagementExport } from '@/hooks/attendance/management/useIncidentManagementExport';
import { useTranslation } from '@/i18n';
import { formatDateToYYYYMMDD, getMinDateByMonths } from '@/utils';


export const IncidentManagementList: React.FC = () => {
  const { t: translate } = useTranslation();

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onSearch,
    onSortChange,
    isLoading,
    errorTypeOptions,
    statusOptions,
    formattedFilterValues,
    handleFilterChange,
    dateFrom,
    dateTo,
    defaultDateFrom,
    defaultDateTo,
    onDateRangeChange,
    employeeSelector,
    employeeSelectorOptions,
    totals,
    errorTypeId,
    status,
    filterEmployeeId,
    searchText,
  } = useIncidentManagementList();

  const { exportIncidents, isExporting } = useIncidentManagementExport({
    dateFrom,
    dateTo,
    errorTypeId,
    status,
    filterEmployeeId,
    searchText,
  });

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="attendance-incident-management-list-page">
        {/* Indicator Cards */}
        <div className="grid grid-cols-5 gap-4">
          <IndicatorCard
            label={translate('attendance:incidentManagement.indicators.pendientes')}
            value={totals.pendientes}
            variant="important"
            data-testid="indicator-management-pendientes"
          />
          <IndicatorCard
            label={translate('attendance:incidentManagement.indicators.aprobados')}
            value={totals.aprobados}
            variant="important"
            data-testid="indicator-management-aprobados"
          />
          <IndicatorCard
            label={translate('attendance:incidentManagement.indicators.rechazados')}
            value={totals.rechazados}
            variant="important"
            data-testid="indicator-management-rechazados"
          />
          <IndicatorCard
            label={translate('attendance:incidentManagement.indicators.error')}
            value={totals.error}
            variant="error"
            data-testid="indicator-management-error"
          />
          <IndicatorCard
            label={translate('attendance:incidentManagement.indicators.todos')}
            value={totals.todos}
            variant="important"
            data-testid="indicator-management-todos"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex flex-wrap gap-3 items-center">
            <DateRangeFilter
              value={{ from: dateFrom, to: dateTo }}
              defaultValue={{ from: defaultDateFrom, to: defaultDateTo }}
              onChange={onDateRangeChange}
              maxDate={formatDateToYYYYMMDD(new Date())}
              minDate={getMinDateByMonths()}
              placeholder={translate('attendance:incidentManagement.filters.dateRangePlaceholder')}
              closeOnComplete
            />
            <FilterDropdown
              renderExtraFilters={() => (
                <div className="flex flex-col gap-4">
                  <Selector
                    label={translate('attendance:incidentManagement.filters.errorType')}
                    value={formattedFilterValues.errorTypeId}
                    onChange={(value) => handleFilterChange('errorTypeId', value as string)}
                    options={errorTypeOptions}
                    data-testid="incident-management-error-type-selector"
                  />
                  <Selector
                    label={translate('attendance:incidentManagement.filters.status')}
                    value={formattedFilterValues.status}
                    onChange={(value) => handleFilterChange('status', value as string)}
                    options={statusOptions}
                    data-testid="incident-management-status-selector"
                  />
                  <Selector
                    label={translate('attendance:incidentManagement.filters.employee')}
                    value={formattedFilterValues.filterEmployeeId}
                    onChange={(value) => handleFilterChange('filterEmployeeId', value as string)}
                    options={employeeSelectorOptions}
                    onLoadMore={employeeSelector.onLoadMore}
                    hasNextPage={employeeSelector.hasNextPage}
                    isLoadingMore={employeeSelector.isLoadingMore}
                    onSearchChange={employeeSelector.onSearchChange}
                    searchable
                    data-testid="incident-management-employee-selector"
                  />
                </div>
              )}
            />
          </div>
          <div data-testid="incident-management-export-button">
            <LinkButton
              iconLeft="import"
              onClick={() => exportIncidents('EXCEL')}
              isLoading={isExporting}
              disabled={total === 0}
            >
              {translate('attendance:incidentManagement.actions.download')}
            </LinkButton>
          </div>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isLoading}
          searchable={true}
          onSearch={onSearch}
          serverSideSort
          onSortChange={onSortChange}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          initialSort={{ key: 'incidentDate', direction: 'desc' }}
          data-testid="table-incident-management"
        />
      </div>
    </PageLayout>
  );
};

export default IncidentManagementList;
